/** Billing API bindings (M10). */
import { http } from "@/shared/api/http";

export type PlanCode = "free" | "starter_v2" | "solo" | "growth" | "pro" | "enterprise_v2";

export interface PlanLimits {
  workspaces: number | null;
  team_seats: number | null;
  storage_gb: number | null;
  text_generations: number | null;
  premium_images_per_month: number | null;
  premium_videos_per_month: number | null;
  video_seconds_per_month: number | null;
  voice_minutes_per_month: number | null;
  publishing_channels: number | null;
  autopilot_max_level: "advisor" | "assistant" | "operator" | "executive";
}

export interface EntitlementPlan {
  key: PlanCode;
  display_name: string;
  tagline: string;
  recommended: boolean;
  hidden: boolean;
  limits: PlanLimits;
  capabilities: string[];
}

export interface Plan extends EntitlementPlan {
  plan_code: PlanCode;
  monthly_price_usd: number | null;
  annual_price_usd: number | null;
  annual_savings_usd: number;
  is_contact_sales: boolean;
}

export interface EffectiveSubscription {
  plan_code: PlanCode;
  plan: EntitlementPlan;
  active: boolean;
  status: string;
  capabilities: string[];
  remaining: Record<string, number | null>;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface Invoice {
  _id: string;
  stripe_invoice_id: string;
  number: string | null;
  amount_paid_cents: number;
  amount_due_cents: number;
  currency: string;
  status: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  period_end: string | null;
}

export const listPlans = (): Promise<Plan[]> => http("/billing/plans");

export const getSubscription = (ws: string): Promise<EffectiveSubscription> =>
  http(`/workspaces/${ws}/billing/subscription`);

export const startCheckout = (
  ws: string, body: { plan_code: PlanCode; interval: "month" | "year"; origin_url: string },
): Promise<{ checkout_url: string; session_id: string }> =>
  http(`/workspaces/${ws}/billing/checkout`, { method: "POST", body });

export const openPortal = (
  ws: string, return_url: string,
): Promise<{ portal_url: string }> =>
  http(`/workspaces/${ws}/billing/portal`, { method: "POST", body: { return_url } });

export const listInvoices = (ws: string): Promise<Invoice[]> =>
  http(`/workspaces/${ws}/billing/invoices`);
